import { useEffect, useState } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import HistoryEduIcon from "@mui/icons-material/HistoryEdu";
import axios from "axios";
import liff from "@line/liff";
import Header from "../components/common/Header";
import Footer from "../components/common/Footer";
import { ensureLiffInit } from "../liffClient";

type Mistake = {
  word: string;
  image_url: string;
  attempted_at: string;
};

export default function QuizMistakes() {
  const [loggedIn, setLoggedIn] = useState(false);
  const [loginFailed, setLoginFailed] = useState(false);
  const [mistakes, setMistakes] = useState<Mistake[] | null>(null);

  useEffect(() => {
    const load = async () => {
      const ready = await ensureLiffInit();
      const isLoggedIn = ready && liff.isLoggedIn();
      setLoggedIn(isLoggedIn);
      if (!isLoggedIn) return;

      try {
        const profile = await liff.getProfile();
        const response = await axios.post("/v1/api/quiz/mistakes/fetch", {
          userId: profile.userId,
        });
        setMistakes([...(response.data.mistakes ?? [])].reverse());
      } catch (error) {
        console.error("Error fetching quiz mistakes:", error);
      }
    };

    load();
  }, []);

  const handleLogin = async () => {
    const ok = await ensureLiffInit();
    if (!ok) {
      setLoginFailed(true);
      return;
    }
    liff.login();
  };

  return (
    <Box sx={{ minHeight: "100vh", backgroundColor: "#f1f5f9", display: "flex", flexDirection: "column" }}>
      <Box sx={{ width: "100%", position: "sticky", top: "0", zIndex: 100 }}>
        <Header />
      </Box>

      <Box sx={{ maxWidth: 560, mx: "auto", px: 2, pt: 4, pb: 2, flex: 1, width: "100%" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 4 }}>
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: "12px",
              background: "linear-gradient(135deg, #4338ca, #6366f1)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: "0 4px 12px rgba(99,102,241,0.4)",
            }}
          >
            <HistoryEduIcon sx={{ color: "white", fontSize: 22 }} />
          </Box>
          <Box>
            <Typography sx={{ fontWeight: 700, fontSize: "1.4rem", color: "#1e293b", lineHeight: 1.2 }}>
              間違えた問題
            </Typography>
            <Typography sx={{ fontSize: "0.8rem", color: "#94a3b8" }}>
              クイズで間違えた単語の一覧
            </Typography>
          </Box>
        </Box>

        {!loggedIn && (
          <Box
            sx={{
              backgroundColor: "white",
              borderRadius: "16px",
              p: 3,
              textAlign: "center",
              boxShadow: "0 2px 12px rgba(0,0,0,0.07)",
              border: "1px solid rgba(226,232,240,0.8)",
            }}
          >
            <Typography sx={{ color: "#64748b", fontSize: "0.9rem", mb: 2 }}>
              ログインすると間違えた問題を確認できます
            </Typography>
            <Button
              onClick={handleLogin}
              variant="contained"
              sx={{
                background: "linear-gradient(135deg, #4338ca, #6366f1)",
                borderRadius: "10px",
                textTransform: "none",
                fontWeight: 600,
                px: 3,
                boxShadow: "0 4px 14px rgba(99,102,241,0.35)",
                "&:hover": { background: "linear-gradient(135deg, #3730a3, #4f46e5)" },
              }}
            >
              ログイン
            </Button>
            {loginFailed && (
              <Typography sx={{ color: "#ef4444", fontSize: "0.78rem", mt: 1.5 }}>
                ログインに接続できませんでした。時間をおいて再度お試しください。
              </Typography>
            )}
          </Box>
        )}

        {loggedIn && mistakes && mistakes.length === 0 && (
          <Typography sx={{ color: "#94a3b8", textAlign: "center", mt: 4 }}>
            間違えた問題はまだありません
          </Typography>
        )}

        {loggedIn &&
          mistakes?.map((item, i) => (
            <Box
              key={i}
              sx={{
                backgroundColor: "white",
                borderRadius: "16px",
                mb: 2.5,
                boxShadow: "0 2px 12px rgba(0,0,0,0.07)",
                border: "1px solid rgba(226,232,240,0.8)",
                overflow: "hidden",
                display: "flex",
                alignItems: "center",
                p: 2.5,
                gap: 2,
              }}
            >
              {item.image_url && (
                <Box
                  component="img"
                  src={item.image_url}
                  alt={item.word}
                  sx={{
                    width: 72,
                    height: 72,
                    objectFit: "cover",
                    borderRadius: "12px",
                    flexShrink: 0,
                    border: "2px solid #f1f5f9",
                  }}
                />
              )}
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontWeight: 700, fontSize: "1.05rem", color: "#1e293b", wordBreak: "break-word" }}>
                  {item.word}
                </Typography>
                <Typography sx={{ fontSize: "0.78rem", color: "#94a3b8", mt: 0.5 }}>
                  {new Date(item.attempted_at).toLocaleString("ja-JP")}
                </Typography>
              </Box>
            </Box>
          ))}
      </Box>

      <Footer />
    </Box>
  );
}
